import { useCallback, useEffect, useState, useSyncExternalStore } from "react"
import { clearRoleAssumption, peekRoleAssumption, setRoleAssumption, subscribeRoleAssumption } from "../data-utils/roleAssumptionStore"

/**
 * React-facing hook for role-assumption state.
 *
 * Exposes the current assumption entry (or null), action helpers, and a
 * countdown-friendly `remainingMs` value. Backed by sessionStorage via
 * useSyncExternalStore so concurrent updates from any source (modal,
 * banner, native expiry, other tab) propagate without prop drilling.
 *
 * The hook runs a 1-second ticker (only while a session is active) to
 * drive countdown UI. When the natural expiry passes, the hook itself
 * clears the store and forces a window reload so every consumer of the
 * Auth0 user object snaps back to the manager's real identity — leaving
 * the operator in a half-state would otherwise yield stale assumed-role
 * chrome on non-subscribing components.
 *
 * Ownership: this hook is the **single owner** of expiry-driven clearing
 * and reload. Other readers (authUtils helpers, authHeadersFor, header
 * chrome) call the pure `getRoleAssumption` which returns null on
 * expiry without mutating storage, so the hook can reliably observe
 * the expired entry via `peekRoleAssumption` and run the cleanup
 * effect regardless of which caller fired first.
 */
export const useRoleAssumption = () => {
  const entry = useSyncExternalStore(subscribeRoleAssumption, peekRoleAssumption, () => null)
  const [now, setNow] = useState(() => Date.now())

  const expiresAtMs = entry?.expiresAt ? Date.parse(entry.expiresAt) : 0
  const expiresAtValid = Number.isFinite(expiresAtMs) && expiresAtMs > 0
  const remainingMs = entry && expiresAtValid ? Math.max(0, expiresAtMs - now) : 0
  // Treat any entry with a non-parseable expiresAt as expired (defensive —
  // store already filters via isPlausibleEntry, but belt-and-braces guard
  // here means we never get stuck in a permanently-active state on bad data).
  const isExpired = !!entry && (!expiresAtValid || remainingMs === 0)

  // While a session is active, tick the countdown every second.
  useEffect(() => {
    if (!entry) return undefined
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [entry])

  // On natural expiry, clear the store and reload so all consumers
  // (AuthenticationGuard, header gating, sidebar, etc.) re-derive from
  // the real Auth0 identity.
  //
  // Both calls are synchronous on purpose. A previous version deferred
  // the reload via `setTimeout(reload, 0)`, but `clearRoleAssumption()`
  // triggers a notify() → re-render → effect-cleanup pipeline that
  // would `clearTimeout` the pending reload before it could fire,
  // leaving the page in a stale assumed-role state. Calling
  // `window.location.reload()` synchronously starts browser navigation
  // immediately, preempting any further React work.
  useEffect(() => {
    if (!isExpired) return undefined
    try {
      clearRoleAssumption()
    } catch {
      // store self-defends; ignore
    }
    try {
      window.location.reload()
    } catch {
      // jsdom / SSR — best-effort
    }
    return undefined
  }, [isExpired])

  const start = useCallback((data) => {
    // Re-throws so the modal can surface storage / validation errors.
    setRoleAssumption(data)
  }, [])

  const stop = useCallback(() => {
    clearRoleAssumption()
  }, [])

  return {
    active: !!entry && !isExpired,
    entry,
    assumed: entry?.assumed ?? null,
    expiresAtMs,
    remainingMs,
    start,
    stop,
  }
}
